import { Injectable } from '@angular/core';
import {
  FrontendApiUpdateRegistrationFlowRequest,
  RegistrationFlow,
} from '@ory/client';
import { Observable } from 'rxjs';
import { OryCreateInput, OryExecuteInput, OryExecuteOutput, OryGetOutput } from '../../model';
import type { OryGetInput } from '../../model';
import { OryKratosBaseFlow } from './base.flow';
import { OryFlowReader } from '../../utils';
import type {
  OryCreatableFlow,
  OryRetrievableFlow,
  OryExecutableFlow,
} from '../flow-capabilities';

/**
 * Implements Ory Kratos registration flow for creating new user accounts.
 *
 * Supports multiple registration methods:
 * - Password registration with email/username
 * - Social sign-up (OIDC providers)
 * - Passwordless (WebAuthn)
 *
 * Successful registration creates identity and may auto-login user depending on configuration.
 */
@Injectable({
  providedIn: 'root'
})
export class OryRegistrationFlow<IdentityTraits extends object = object>
  extends OryKratosBaseFlow
  implements
    OryCreatableFlow<'registration', IdentityTraits>,
    OryRetrievableFlow<'registration', IdentityTraits>,
    OryExecutableFlow<'registration', IdentityTraits>
{
  create(
    input: OryCreateInput<'registration'> = {},
  ): Observable<RegistrationFlow> {
    return this.fromOryRequest(this.iamApi.createBrowserRegistrationFlow(input));
  }

  get(input: OryGetInput<'registration'>): Observable<RegistrationFlow> {
    return this.fromOryRequest(this.iamApi.getRegistrationFlow(input));
  }

  execute(
    flow: OryGetOutput<'registration', IdentityTraits>,
    input: OryExecuteInput<'registration'>,
  ): Observable<OryExecuteOutput<'registration', IdentityTraits>> {
    const csrfToken = OryFlowReader.csrfToken(flow);
    const request: FrontendApiUpdateRegistrationFlowRequest = {
      flow: flow.id,
      updateRegistrationFlowBody: {
        ...input,
        csrf_token: csrfToken
      }
    };

    return this.fromOryRequest(this.iamApi.updateRegistrationFlow(request));
  }

}
